'use client';

import { Check, Layers3 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { useEditorStore } from '@/lib/editor/editorStore';
import { getFontEntry } from '@/lib/editor/fonts';
import {
  OVERLAY_TEMPLATES,
  type OverlayTemplate,
  type OverlayTemplateCategory,
  type OverlayTemplateLayer,
} from '@/lib/editor/overlayTemplates';

const CATEGORIES: Array<'Все' | OverlayTemplateCategory> = ['Все', 'Продажи', 'Соцсети', 'Контент', 'События'];

function previewBadgeStyle(layer: OverlayTemplateLayer) {
  if (layer.type !== 'sticker') return undefined;
  const radius = layer.badgeStyle === 'pill' ? 999 : layer.badgeStyle === 'soft-square' ? 3 : 6;
  return {
    background: layer.badgeAccentColor
      ? `linear-gradient(135deg, ${layer.backgroundColor}, ${layer.badgeAccentColor})`
      : layer.backgroundColor,
    border: layer.badgeBorderWidth
      ? `${Math.max(1, layer.badgeBorderWidth / 3)}px solid ${layer.badgeBorderColor ?? 'transparent'}`
      : undefined,
    borderRadius: radius,
    padding: '3px 6px',
    boxShadow: '0 4px 12px rgba(0,0,0,.28)',
  };
}

export function TemplatesPanel() {
  const { t } = useI18n();
  const applyOverlayTemplate = useEditorStore((s) => s.applyOverlayTemplate);
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]>('Все');
  const [appliedId, setAppliedId] = useState<string | null>(null);
  const appliedResetRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (appliedResetRef.current) clearTimeout(appliedResetRef.current);
  }, []);
  const templates = useMemo(
    () => category === 'Все' ? OVERLAY_TEMPLATES : OVERLAY_TEMPLATES.filter((item) => item.category === category),
    [category]
  );

  function handleApply(template: OverlayTemplate) {
    applyOverlayTemplate(template);
    // Transient confirmation flash — the badge isn't a persistent "this template
    // is on the timeline" state (templates create plain layers with no back-ref,
    // so it can't be reconciled after edits/deletes); reset it so it can't go stale.
    setAppliedId(template.id);
    if (appliedResetRef.current) clearTimeout(appliedResetRef.current);
    appliedResetRef.current = setTimeout(() => setAppliedId(null), 1500);
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-[#121214] text-white">
      <div className="flex h-10 shrink-0 items-center border-b border-white/10 px-3.5">
        <p className="text-sm font-semibold">{t('studio.editor.toolTemplates')}</p>
      </div>

      <div data-lenis-prevent className="min-h-0 flex-1 overflow-y-auto p-3">
        <p className="text-xs leading-5 text-white/60">
          Готовые многослойные сцены. Все элементы остаются редактируемыми после добавления.
        </p>

        <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1">
          {CATEGORIES.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setCategory(item)}
              className={`shrink-0 rounded-full px-2.5 py-1.5 text-[10px] font-bold transition ${
                category === item ? 'bg-brand-500 text-white' : 'bg-white/[0.06] text-white/55 hover:text-white'
              }`}
            >
              {item}
            </button>
          ))}
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2.5">
          {templates.map((template) => {
            const wasApplied = appliedId === template.id;
            return (
              <button
                key={template.id}
                type="button"
                onClick={() => handleApply(template)}
                className={`group overflow-hidden rounded-xl border text-left transition ${
                  wasApplied
                    ? 'border-brand-400 bg-brand-500/10'
                    : 'border-white/10 bg-white/[0.035] hover:border-brand-500/70 hover:bg-white/[0.07]'
                }`}
              >
                <div
                  className="relative aspect-[9/12] w-full overflow-hidden border-b border-white/10"
                  style={{ background: template.previewBackground }}
                >
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_18%,rgba(255,255,255,.12),transparent_33%)]" />
                  {template.layers.map((layer, index) => {
                    const font = getFontEntry(layer.fontFamily);
                    return (
                      <span
                        key={`${template.id}-${index}`}
                        className="absolute max-w-[92%] -translate-x-1/2 -translate-y-1/2 whitespace-pre-line text-center leading-[0.92]"
                        style={{
                          left: `${layer.x}%`,
                          top: `${layer.y}%`,
                          fontFamily: font.cssVar,
                          fontWeight: layer.fontWeight,
                          fontStyle: layer.fontStyle,
                          fontSize: `${Math.max(5, layer.size / 14)}px`,
                          color: layer.color,
                          opacity: layer.opacity ?? 1,
                          transform: `translate(-50%, -50%) rotate(${layer.rotation ?? 0}deg)`,
                          textDecoration: layer.underline ? 'underline' : undefined,
                          ...previewBadgeStyle(layer),
                        }}
                      >
                        {layer.text}
                      </span>
                    );
                  })}
                  <div className="absolute bottom-1.5 right-1.5 flex items-center gap-1 rounded-full bg-black/45 px-1.5 py-1 text-[8px] text-white/70 backdrop-blur">
                    {wasApplied ? <Check className="h-2.5 w-2.5 text-emerald-300" /> : <Layers3 className="h-2.5 w-2.5" />}
                    {template.layers.length}
                  </div>
                </div>
                <div className="px-2.5 py-2.5">
                  <p className="truncate text-[11px] font-bold text-white/90">{template.name}</p>
                  <p className="mt-0.5 truncate text-[9px] text-white/40">{template.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
